import React, { useEffect, useState, useMemo, createRef } from 'react';
import { useHistory } from 'react-router-dom';
import Header from '../../components/common/global-top-bar';
import { CustomInput, ToggleButton, UseFormDropDown } from '../../components/common';
import SwitchBar from '../../components/switch-bar';
import { HeaderOptions } from './clients-constants';
import SaveButton from './switch-bar-button';
import HeadBack from './redirect';
import Spinner from '../../components/common/spinner';
import RichTextEditor from '../../components/rich-text-editor/rich-text-editor';
import { toast } from 'react-toastify';
import RouteLeavingGuard from '../../components/route-leaving-prompt';
import { ROUTE_PATH } from '../../routes/paths';
import Modal from '../../components/common/modal';
import {
  clientAIModels,
  useClient,
  useCreateClient,
  useUpdateClient,
  useVerticals,
  useDomains,
} from '../../hooks/useClients';
import { useTiers } from '../../hooks/useTiers';
import { Button } from '../../components/ui';
import { useSyncHubspot } from '../../hooks/useClients';
import { TagExtended, useTags } from '../../hooks/useTags';
import { useForm, Controller } from 'react-hook-form';

const clientStatus = [
  { label: 'INACTIVE', key: 'inactive' },
  { label: 'SETUP', key: 'setup' },
  { label: 'LIVE', key: 'active' },
];

const Configure: React.FunctionComponent = (): JSX.Element => {
  const history = useHistory();
  const mutateSyncHubspot = useSyncHubspot();
  const initialClientId = localStorage.getItem('clientid');
  const [clientId, setClientId] = useState<string | null>(initialClientId);
  const { data: client, isFetching: clientFetching, refetch } = useClient(clientId ? parseInt(clientId) : undefined);
  const { createClient } = useCreateClient();
  const { updateClient } = useUpdateClient(parseInt(clientId!));
  const { data: tags, isLoading: tagsLoading } = useTags({ include_hidden: true });
  const { data: verticals, isFetching: verticalsFetching } = useVerticals();
  const { data: domains, isFetching: domainsFetching } = useDomains();
  const { data: tiers, isFetching: tiersFetching } = useTiers();

  const [saveLocationPath, setSaveLocationPath] = useState<string>('');
  const [tagsModal, setTagsModal] = useState(false);

  const fileInputGroupGraphic: React.RefObject<HTMLInputElement> = createRef();
  const fileInputWelcomeImage: React.RefObject<HTMLInputElement> = createRef();
  const fileInputWelcomeVideo: React.RefObject<HTMLInputElement> = createRef();
  const fileInputVideoHostImage: React.RefObject<HTMLInputElement> = createRef();
  const fileInputPoweredByHeader: React.RefObject<HTMLInputElement> = createRef();
  const fileInputGroupBanner: React.RefObject<HTMLInputElement> = createRef();

  const defaultClient = useMemo(
    () => ({
      ai_model: client?.ai_model || 'traditional_search',
      active: client?.status || 'setup',
      clientCode: client?.client_code || undefined,
      clientName: client?.name || '',
      contractDate: client?.contract_date || null,
      contractIncludesComms: client?.contract_includes_comms || false,
      crmSync: client?.crm_sync || false,
      enableEligibility: !!!client?.eligibility_config?.hide_client_admin_eligibility,
      goLiveDate: client?.go_live_date || null,
      groupBannerEnabled: client?.group_banner_enabled || false,
      groupBannerImage: client?.group_banner_file_url || null,
      groupGraphic: client?.logo_file_url || null,
      verticalId: client?.vertical?.id || '',
      domainId: client?.domain_id ?? client?.domain?.id ?? '',
      peerOnboardingTierId: client?.peer_onboarding_tier_id ?? '',
      listenerScope: client?.listener_scope || 'global',
      privatePeerContent: client?.private_peer_content || false,
      contentOptional: client?.content_optional || false,
      listenersOnly: client?.listeners_only || false,
      internalNotes: client?.notes || '',
      isTest: client?.is_test || false,
      memberSelfEnroll: client?.member_self_enrollment || false,
      poweredByHeaderEnabled: client?.powered_by_header_enabled || false,
      poweredByHeaderImage: client?.powered_by_header_file_url || null,
      resellerName: client?.reseller || '',
      richText: client?.landing_page_content || '',
      tagsArray:
        tags?.map((item: any) => ({
          ...item,
          hasTag: client?.allowed_listener_tags?.includes(item.id) || false,
        })) || [],
      useMfa: client?.use_mfa || false,
      videoConfigurationTitle: client?.video_configuration?.title || '',
      videoHostImage: client?.video_configuration?.author_image_url || null,
      videoHostName: client?.video_configuration?.author_name || '',
      welcomeImage: client?.video_configuration?.image_url || null,
      welcomeVideo: client?.video_configuration?.video_url || null,
      welcomeVideoEnabled: client?.video_configuration?.enabled || false,
    }),
    [client, tags],
  );

  const {
    control,
    formState: { errors, isDirty },
    getValues,
    handleSubmit,
    register,
    reset,
    setValue,
    watch,
  } = useForm({
    defaultValues: useMemo(() => {
      if (client) {
        return defaultClient;
      }
    }, [client]),
  });

  const syncHubspot = (clientId: number) => {
    mutateSyncHubspot.mutate(clientId, {
      onSuccess: () => {
        toast.success('Hubspot Synced Successfully');
      },
      onError: (error: any) => {
        toast.error(error.response.data.description);
      },
    });
  };

  type imageUploadConfigKey =
    | 'groupBannerImage'
    | 'groupGraphic'
    | 'poweredByHeaderImage'
    | 'videoHostImage'
    | 'welcomeImage'
    | 'welcomeVideo';

  const imageUploadConfig: Record<
    imageUploadConfigKey,
    {
      formField: string;
      dimensions?: { width: number; height: number } | null;
      aspectRatio?: { width: number; height: number };
    }
  > = {
    groupBannerImage: { formField: 'groupBannerImage', dimensions: null },
    groupGraphic: { formField: 'groupGraphic', aspectRatio: { width: 2, height: 1 } },
    poweredByHeaderImage: { formField: 'poweredByHeaderImage', dimensions: null },
    videoHostImage: { formField: 'videoHostImage', dimensions: { width: 720, height: 720 } },
    welcomeImage: { formField: 'welcomeImage', dimensions: { width: 1280, height: 720 } },
    welcomeVideo: { formField: 'welcomeVideo', dimensions: null },
  };

  const inputRefs: Record<imageUploadConfigKey, React.RefObject<HTMLInputElement>> = {
    groupBannerImage: fileInputGroupBanner,
    groupGraphic: fileInputGroupGraphic,
    poweredByHeaderImage: fileInputPoweredByHeader,
    videoHostImage: fileInputVideoHostImage,
    welcomeImage: fileInputWelcomeImage,
    welcomeVideo: fileInputWelcomeVideo,
  };

  const onFileChange = async (event: any, name: imageUploadConfigKey) => {
    try {
      if (event.target.files && event.target.files[0]) {
        const reader = new FileReader();
        const config = imageUploadConfig[name];
        const { dimensions, aspectRatio, formField } = config;

        reader.onload = (e: any) => {
          // We handle image and video uploads separately
          if (e.target.result.startsWith('data:image')) {
            const image = new Image();

            image.onload = () => {
              const { width, height } = image;

              if (dimensions && (width !== dimensions.width || height !== dimensions.height)) {
                toast.error(`Height and Width must be ${dimensions.width} x ${dimensions.height}.`);
                return;
              }

              if (aspectRatio) {
                const expectedRatio = aspectRatio.width / aspectRatio.height;
                const actualRatio = width / height;
                if (Math.abs(actualRatio - expectedRatio) > 0.01) {
                  toast.error(`Image must have a ${aspectRatio.width}:${aspectRatio.height} aspect ratio.`);
                  return;
                }
              }

              setValue(formField as keyof typeof defaultClient, e.target.result, { shouldDirty: true });
            };
            image.src = e.target.result;
          } else {
            setValue(formField as keyof typeof defaultClient, e.target.result, { shouldDirty: true });
          }
        };

        reader.readAsDataURL(event.target.files[0]);
      }
    } catch (error) {
      console.error('err--', error);
    }
  };

  const handleImageUpload = (name: imageUploadConfigKey) => {
    inputRefs[name]?.current?.click();
  };

  const handleTags = (hasTag: boolean, id: number) => {
    const updatedTags = watch('tagsArray').map((item: TagExtended) => {
      if (item.id === id) {
        return { ...item, hasTag };
      } else {
        return item;
      }
    });
    setValue('tagsArray', updatedTags, { shouldDirty: true });
  };

  const handleSave = (data: any) => {
    if (data.welcomeVideoEnabled && (!data.welcomeVideo || !data.welcomeImage)) {
      toast.error('Please add welcome video & image');
      return;
    }
    const allowedTags = data.tagsArray.filter((item: TagExtended) => item.hasTag).map((item: any) => item.id);
    const payload = {
      ai_model: data.ai_model,
      allowed_listener_tags: allowedTags,
      client_code: data.clientCode || undefined,
      contract_date: data.contractDate,
      contract_includes_comms: data.contractIncludesComms,
      crm_sync: data.crmSync,
      eligibility_config: {
        hide_client_admin_eligibility: !data.enableEligibility,
      },
      go_live_date: data.goLiveDate,
      group_banner_enabled: data.groupBannerEnabled,
      group_banner_file_url: data.groupBannerImage,
      is_test: data.isTest,
      landing_page_content: data.richText,
      logo_file_url: data.groupGraphic,
      member_self_enrollment: data.memberSelfEnroll,
      name: data.clientName?.trim(),
      notes: data.internalNotes,
      powered_by_header_enabled: data.poweredByHeaderEnabled,
      powered_by_header_file_url: data.poweredByHeaderImage,
      reseller: data.resellerName?.trim(),
      status: data.active,
      use_mfa: data.useMfa,
      vertical_id: data.verticalId,
      domain_id: data.domainId || undefined,
      peer_onboarding_tier_id:
        data.peerOnboardingTierId === '' || data.peerOnboardingTierId === undefined
          ? null
          : Number(data.peerOnboardingTierId),
      listener_scope: data.listenerScope || 'global',
      private_peer_content: Boolean(data.privatePeerContent),
      content_optional: Boolean(data.contentOptional),
      listeners_only: Boolean(data.listenersOnly),
      video_configuration: {
        author_image_url: data.videoHostImage,
        author_name: data.videoHostName?.trim(),
        enabled: data.welcomeVideoEnabled,
        image_url: data.welcomeImage,
        title: data.videoConfigurationTitle?.trim(),
        video_url: data.welcomeVideo,
      },
    };
    if (clientId) {
      updateClient.mutate(payload, {
        onSuccess: () => {
          if (saveLocationPath !== '') {
            history.push(saveLocationPath);
          }
          refetch().then(() => {
            reset(defaultClient);
          });
        },
        onError: (error: any) => {
          toast.error(error.response.data.description);
        },
      });
    } else {
      createClient.mutate(payload, {
        onSuccess: (response) => {
          if (response && response.id) {
            localStorage.setItem('clientid', response.id.toString());
            setClientId(response.id.toString());
          }
          if (saveLocationPath !== '') {
            history.push(saveLocationPath);
          }
          reset(getValues());
        },
        onError: (error: any) => {
          toast.error(error.response.data.description);
        },
      });
    }
  };

  useEffect(() => {
    if (saveLocationPath!) {
      history.push(saveLocationPath);
    }
    if (client || tags) {
      reset(defaultClient);
    }
  }, [client, tags]);

  return (
    <div>
      {(clientFetching || tagsLoading || verticalsFetching || domainsFetching || tiersFetching) && <Spinner />}
      <Header heading={<HeadBack clientName={watch('clientName')} />} />
      <SwitchBar
        heading={HeaderOptions}
        position={1}
        isDisabled={clientId ? false : true}
        optionalButton={
          <div className="w-1/12">
            <SaveButton onSave={handleSubmit(handleSave)} disable={!isDirty} />
          </div>
        }
      />
      <div className="max-window-height overflow-y-auto pb-32 pt-4">
        <form>
          <div className="flex justify-between">
            <div className="flex w-1/2 gap-4 px-7">
              <div className="w-1/2 py-4">
                <CustomInput
                  Label={'GROUP NAME'}
                  labelClass="font-bold"
                  inputType={'text'}
                  register={{
                    ...register('clientName', {
                      required: true,
                    }),
                  }}
                  Error={errors.clientName}
                  ErrorMessage={'Field is Required'}
                  dataTestId="group-name"
                />
              </div>
              <div className="w-1/2 py-4">
                <CustomInput
                  Label={'RESELLER NAME'}
                  labelClass="font-bold"
                  inputType={'text'}
                  register={{
                    ...register('resellerName', {}),
                  }}
                  Error={errors.resellerName}
                  ErrorMessage={'Field is Required'}
                  dataTestId="reseller-name"
                />
              </div>
            </div>
            <div className="w-1/2 px-7">
              <p className="text-gray-dark py-2 font-bold">STATUS</p>
              <div className="flex w-full justify-start gap-x-4">
                {clientStatus.map((item: any) => (
                  <Controller
                    key={item.key}
                    name="active"
                    control={control}
                    render={({ field }) => (
                      <button
                        className={
                          'rounded-full px-5 py-1 font-medium text-white' +
                          (item.key === field.value
                            ? item.label === 'SETUP'
                              ? ' stacked-bar-orange-primary'
                              : item.label === 'INACTIVE'
                                ? ' stacked-bar-red-primary'
                                : ' stacked-bar-green-primary'
                            : ' bg-gray-300')
                        }
                        onClick={(e) => {
                          e.preventDefault();
                          field.onChange(item.key);
                        }}
                      >
                        {item.label}
                      </button>
                    )}
                  />
                ))}
              </div>
            </div>
          </div>
          <div className="flex w-1/2 gap-4 px-7">
            <div className="w-1/2 py-4">
              <CustomInput
                Label={'CONTRACT DATE'}
                labelClass="font-bold"
                inputType={'date'}
                register={{
                  ...register('contractDate', {
                    required: true,
                  }),
                }}
                Error={errors.contractDate}
                ErrorMessage={'Field is Required'}
                dataTestId="contract-date"
              />
            </div>
            <div className="w-1/2 py-4">
              <CustomInput
                Label={'GO LIVE DATE'}
                labelClass="font-bold"
                inputType={'date'}
                register={{
                  ...register('goLiveDate', {
                    required: true,
                  }),
                }}
                Error={errors.goLiveDate}
                ErrorMessage={'Field is Required'}
                dataTestId="go-live-date"
              />
            </div>
          </div>
          <div className="flex w-1/2 gap-4 px-7">
            <div className="w-1/2 py-4">
              <CustomInput
                Label={'GROUP CODE'}
                labelClass="font-bold"
                inputType={'text'}
                register={{
                  ...register('clientCode'),
                }}
                dataTestId="group-code"
              />
            </div>
            <div className="w-1/2 py-4">
              <UseFormDropDown label={'Industry / Vertical'} labelClass="font-bold leading-4 text-gray-dark uppercase">
                <select
                  id="industry-vertical"
                  data-testid="industry-vertical"
                  className="select-box w-full focus:ring-transparent"
                  {...register('verticalId')}
                  defaultValue={watch('verticalId')}
                >
                  <option value="">Select an industry / vertical</option>
                  {verticals &&
                    verticals.length > 0 &&
                    verticals.map((vertical) => (
                      <option key={vertical.key} value={vertical.id}>
                        {vertical.name}
                      </option>
                    ))}
                </select>
              </UseFormDropDown>
            </div>
          </div>
          <div className="flex w-1/2 gap-4 px-7">
            <div className="w-1/2 py-4">
              <UseFormDropDown label={'Domain'} labelClass="font-bold leading-4 text-gray-dark uppercase">
                <select
                  id="domain"
                  data-testid="domain"
                  className="select-box w-full focus:ring-transparent"
                  {...register('domainId')}
                  defaultValue={watch('domainId')}
                >
                  <option value="">Select a domain</option>
                  {domains &&
                    domains.length > 0 &&
                    domains.map((domain) => (
                      <option key={domain.key} value={domain.id}>
                        {domain.name}
                      </option>
                    ))}
                </select>
              </UseFormDropDown>
            </div>
            <div className="w-1/2 py-4">
              <UseFormDropDown
                label={'Peer onboarding tier (peer group link)'}
                labelClass="font-bold leading-4 text-gray-dark uppercase"
              >
                <select
                  id="peer-onboarding-tier"
                  data-testid="peer-onboarding-tier"
                  className="select-box w-full focus:ring-transparent"
                  {...register('peerOnboardingTierId')}
                  defaultValue={watch('peerOnboardingTierId')}
                >
                  <option value="">Use default tier</option>
                  {tiers &&
                    tiers.length > 0 &&
                    tiers.map((tier) => (
                      <option key={tier.id} value={tier.id}>
                        {tier.name}
                      </option>
                    ))}
                </select>
              </UseFormDropDown>
            </div>
          </div>
          <div className="flex w-1/2 gap-4 px-7">
            <div className="w-1/2 py-4">
              <UseFormDropDown
                label={'Group vs Global Listeners'}
                labelClass="font-bold leading-4 text-gray-dark uppercase"
              >
                <select
                  id="listener-scope"
                  data-testid="listener-scope"
                  className="select-box w-full focus:ring-transparent"
                  {...register('listenerScope')}
                  defaultValue={watch('listenerScope')}
                >
                  <option value="global">Global Listeners (domain-available)</option>
                  <option value="group">Group Listeners (affiliated only)</option>
                </select>
              </UseFormDropDown>
              <p className="text-gray-dark pt-1 text-sm font-normal">
                Controls who can take calls for this group&apos;s members. Does not change the content library.
              </p>
            </div>
            <div className="w-1/2 py-4">
              <div className="flex justify-between">
                <p className="text-gray-dark py-2 text-left font-bold uppercase leading-4">Private Peer Content</p>
                <ToggleButton
                  id={'private-peer-content'}
                  setStatus={(e) => {
                    setValue('privatePeerContent', e, { shouldDirty: true });
                  }}
                  status={watch('privatePeerContent')}
                />
              </div>
              <p className="text-gray-dark pt-1 text-sm font-normal">
                When on, this group&apos;s affiliated peers&apos; audio and video only surface to this group&apos;s
                members.
              </p>
            </div>
          </div>
          <div className="flex w-1/2 gap-4 px-7">
            <div className="w-1/2 py-4">
              <div className="flex justify-between">
                <p className="text-gray-dark py-2 text-left font-bold uppercase leading-4">Content Optional</p>
                <ToggleButton
                  id={'content-optional'}
                  setStatus={(e) => {
                    setValue('contentOptional', e, { shouldDirty: true });
                  }}
                  status={watch('contentOptional')}
                />
              </div>
              <p className="text-gray-dark pt-1 text-sm font-normal">
                When on, affiliated peers may become Peer Listeners without uploading audio/video experiences.
              </p>
            </div>
            <div className="w-1/2 py-4">
              <div className="flex justify-between">
                <p className="text-gray-dark py-2 text-left font-bold uppercase leading-4">Listeners Only</p>
                <ToggleButton
                  id={'listeners-only'}
                  setStatus={(e) => {
                    setValue('listenersOnly', e, { shouldDirty: true });
                  }}
                  status={watch('listenersOnly')}
                />
              </div>
              <p className="text-gray-dark pt-1 text-sm font-normal">
                When on, members see listeners and calls only. Content, collections, and resources are hidden.
              </p>
            </div>
          </div>
        </form>
        <div className="flex justify-between">
          <div className="w-1/2 px-7">
            <p className="text-gray-dark py-2 font-bold">GLOBAL GRAPHICS</p>
            <hr className="bg-gray-dark"></hr>
            <p className="text-gray-dark pt-1 font-normal">GROUP GRAPHIC</p>
            <p className="text-gray-dark py-2 pt-1 font-normal">
              This is the client's logo that displays in the MWA anywhere we are showing a group graphic.
            </p>
            <div className="flex w-full">
              <div
                className={`${
                  !watch('groupGraphic') ? 'cursor-pointer' : ''
                } bg-gray-background-light aspect-ratio-container w-full rounded-sm`}
                onClick={() => handleImageUpload('groupGraphic')}
              >
                {watch('groupGraphic') ? (
                  <div className="aspect-ratio-div">
                    <img className="aspect-ratio-container-img" src={watch('groupGraphic') || ''} alt="clientsImage" />
                  </div>
                ) : (
                  <div className="flex h-56 items-center justify-center">
                    <button className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl">
                      +
                    </button>
                    <input
                      ref={fileInputGroupGraphic}
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onFileChange(e, 'groupGraphic')}
                      onClick={() => {
                        if (fileInputGroupGraphic.current) {
                          fileInputGroupGraphic.current.value = '';
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="pl-4">
                <p className="text-gray-dark pt-4 font-normal">2:1 Aspect Ratio</p>
                <div className="aspect-ratio-container w-full pt-2">
                  {watch('groupGraphic') && (
                    <button
                      className="my-2 w-full rounded-full bg-gray-300 p-2 text-center text-sm text-white"
                      onClick={() => {
                        setValue('groupGraphic', '', { shouldDirty: true });
                      }}
                    >
                      REMOVE
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="w-1/2 px-7">
            <div className="flex justify-between">
              <p className="text-gray-dark py-2 text-left">For Testing:</p>
              <ToggleButton
                id={'isTest'}
                setStatus={(e) => {
                  setValue('isTest', e, { shouldDirty: true });
                }}
                status={watch('isTest')}
              />
            </div>
            <div className="flex justify-between">
              <p className="text-gray-dark py-2 text-left">Enable Client Admin Eligibility Access</p>
              <ToggleButton
                id={'EligibilityAccess'}
                setStatus={(e) => {
                  setValue('enableEligibility', e, { shouldDirty: true });
                }}
                status={watch('enableEligibility')}
              />
            </div>
            <div className="flex justify-between pt-2">
              <p className="text-gray-dark py-2 text-left">Enable Client for Member Self Enroll</p>
              <ToggleButton
                id={'self-enroll'}
                setStatus={(e) => {
                  setValue('memberSelfEnroll', e, { shouldDirty: true });
                }}
                status={watch('memberSelfEnroll')}
              />
            </div>
            <div className="flex justify-between pt-2">
              <p className="text-gray-dark py-2 text-left">Enable CRM Sync</p>
              <ToggleButton
                id={'crm-sync'}
                setStatus={(e) => {
                  setValue('crmSync', e, { shouldDirty: true });
                }}
                status={watch('crmSync')}
              />
            </div>
            <div className="flex justify-between pt-2">
              <p className="text-gray-dark py-2 text-left">Contract Includes Comms</p>
              <ToggleButton
                id={'contract-includes-comms'}
                setStatus={(e) => {
                  setValue('contractIncludesComms', e, { shouldDirty: true });
                }}
                status={watch('contractIncludesComms')}
              />
            </div>
            <div className="flex justify-between pt-2">
              <p className="text-gray-dark py-2 text-left">Enable MFA</p>
              <ToggleButton
                id={'enable_mfa'}
                setStatus={(e) => {
                  setValue('useMfa', e, { shouldDirty: true });
                }}
                status={watch('useMfa')}
              />
            </div>
            {client?.ai_model && (
              <div className="flex flex-col">
                <label className="mb-2" htmlFor="search_model">
                  Search Query Model
                </label>
                <select
                  className="mb-4"
                  id="search_model"
                  onChange={(e) => {
                    setValue('ai_model', e.target.value as (typeof clientAIModels)[number], { shouldDirty: true });
                  }}
                  defaultValue={client.ai_model}
                >
                  {clientAIModels.map((model) => {
                    return (
                      <option key={model} data-testid={`search-category-${model}`}>
                        {model}
                      </option>
                    );
                  })}
                </select>
              </div>
            )}
            <div className="flex justify-between">
              <p className="text-gray-dark py-2 text-left font-bold">REQUIRED LISTENER TAGS</p>
              <button
                className="text-blue-primary py-2 text-right font-bold"
                onClick={() => {
                  setTagsModal(true);
                }}
              >
                + Add
              </button>
            </div>
            <hr className="bg-gray-dark"></hr>
            <Controller
              name="tagsArray"
              control={control}
              render={({ field }) => (
                <div className="-ml-2 w-full flex-wrap space-x-2 space-y-2 text-sm">
                  {field.value?.length > 0 && field.value.filter((tag: TagExtended) => tag.hasTag).length > 0 ? (
                    field.value
                      .filter((tag: any) => tag.hasTag)
                      .map((item: TagExtended, index: number) => {
                        return (
                          <button
                            key={item.id}
                            className={`bg-citrus rounded-md text-sm font-bold text-white ${
                              index === 0 ? 'ml-2 mt-2' : ''
                            }`}
                            onClick={() => {
                              const updatedTags = field.value.map((tag: TagExtended) =>
                                tag.id === item.id ? { ...tag, hasTag: false } : tag,
                              );
                              field.onChange(updatedTags);
                            }}
                          >
                            <div className="flex px-3 py-2">
                              <div className="grow pr-2">{item.name}</div>
                              <div className="grow-0">x</div>
                            </div>
                          </button>
                        );
                      })
                  ) : (
                    <p className="m-2"> No Listener Tags </p>
                  )}
                </div>
              )}
            />
            <p className="text-gray-dark py-2 pt-4 text-left font-bold">Peer Group Link</p>
            <hr className="bg-gray-dark"></hr>
            <div
              title="copy"
              onClick={() => {
                navigator.clipboard.writeText(
                  `${process.env.REACT_APP_LISTENER_LINK}/onboarding/client-route/${clientId}/${encodeURI(watch('clientName'))}`,
                );
                toast.success('Link Copied');
              }}
              className="w-full cursor-pointer flex-wrap space-x-2 space-y-2 pt-4"
            >
              {process.env.REACT_APP_LISTENER_LINK}/onboarding/client-route/{clientId}/{encodeURI(watch('clientName'))}
            </div>
            {watch('crmSync') && (
              <div>
                <p className="text-gray-dark py-2 pt-4 text-left font-bold">Sync with Hubspot</p>
                <hr className="bg-gray-dark"></hr>
                <Button className="mt-4" variant="primary" onClick={() => syncHubspot(Number(clientId))}>
                  Sync
                </Button>
              </div>
            )}
          </div>
        </div>
        <div className="mt-5 w-full px-7">
          <div className="mt-4 w-full">
            <p className="text-gray-dark pt-4 font-bold">LANDING PAGE MESSAGE</p>
            <div className="mt-4">
              <RichTextEditor
                content={watch('richText')}
                onChange={(value) => setValue('richText', value, { shouldDirty: true })}
                key={`rich-text-${clientId}`}
              />
            </div>
          </div>
        </div>
        <div className="mt-8 w-full px-7">
          <div className="flex w-full items-center justify-between">
            <p className="text-gray-dark pt-4 font-bold">WELCOME VIDEO</p>
            <div>
              <ToggleButton
                id={'welcomeVideoEnabled'}
                setStatus={(e) => {
                  setValue('welcomeVideoEnabled', e, { shouldDirty: true });
                }}
                status={watch('welcomeVideoEnabled')}
              />
            </div>
          </div>
          <hr className="bg-gray-dark"></hr>
          <div className="relative">
            {!watch('welcomeVideoEnabled') && <div className="absolute z-50 h-full w-full bg-white opacity-50"></div>}
            <div className="flex w-full">
              <div className="w-1/2">
                <p className="text-gray-dark pb-4 pt-4 font-medium">WELCOME VIDEO THUMBNAIL</p>
                <div className="flex">
                  <div
                    className={`${
                      watch('welcomeImage') ? '' : 'cursor-pointer'
                    } bg-gray-background-light aspect-ratio-container w-full rounded-sm`}
                    onClick={() => handleImageUpload('welcomeImage')}
                  >
                    {watch('welcomeImage') ? (
                      <div className="aspect-ratio-div">
                        <img
                          className="aspect-ratio-container-img"
                          src={watch('welcomeImage') || ''}
                          alt="welcomeImage"
                        />
                      </div>
                    ) : (
                      <div className="flex h-56 items-center justify-center">
                        <button className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl">
                          +
                        </button>
                        <input
                          ref={fileInputWelcomeImage}
                          id="file-upload-group-promotion-image"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => onFileChange(e, 'welcomeImage')}
                          onClick={() => {
                            if (fileInputWelcomeImage.current) {
                              fileInputWelcomeImage.current.value = '';
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="pl-4">
                    <p className="text-gray-dark pt-4 font-normal">1280 x 720 px</p>
                    {watch('welcomeImage') && (
                      <div className="aspect-ratio-container w-full">
                        <button
                          className="my-2 w-full rounded-full bg-gray-300 p-2 text-center text-sm text-white"
                          onClick={() => {
                            setValue('welcomeImage', '', { shouldDirty: true });
                          }}
                        >
                          REMOVE
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="w-1/2">
                <p className="text-gray-dark pb-4 pt-4 font-medium">WELCOME VIDEO FILE</p>
                <div className="flex">
                  <div
                    className={`${
                      watch('welcomeVideo') ? '' : 'cursor-pointer'
                    } bg-gray-background-light aspect-ratio-container w-full rounded-sm`}
                    onClick={() => handleImageUpload('welcomeVideo')}
                  >
                    {watch('welcomeVideo') ? (
                      <div className="">
                        <video width="100%" controls>
                          <source src={watch('welcomeVideo') || ''} />
                          Your browser does not support HTML5 video.
                        </video>
                      </div>
                    ) : (
                      <div className="flex h-56 w-full items-center justify-center">
                        <button className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl">
                          +
                        </button>
                        <input
                          ref={fileInputWelcomeVideo}
                          id="file-upload-promotion-overlay-video"
                          accept="video/mp4,video/x-m4v,video/*"
                          type="file"
                          className="hidden"
                          onChange={(e) => onFileChange(e, 'welcomeVideo')}
                          onClick={() => {
                            if (fileInputWelcomeVideo.current) {
                              fileInputWelcomeVideo.current.value = '';
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="pl-4">
                    {watch('welcomeVideo') && (
                      <div className="aspect-ratio-container w-full">
                        <button
                          className="my-2 w-full rounded-full bg-gray-300 p-2 text-center text-sm text-white"
                          onClick={() => {
                            setValue('welcomeVideo', '', { shouldDirty: true });
                          }}
                        >
                          REMOVE
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="flex w-full">
              <div className="w-1/2">
                <p className="text-gray-dark pb-4 pt-4 font-medium">VIDEO HOST IMAGE</p>
                <div className="flex">
                  <div
                    className={`${
                      watch('videoHostImage') ? '' : 'cursor-pointer'
                    } bg-gray-background-light aspect-ratio-container h-56 w-56 rounded-sm`}
                    onClick={() => handleImageUpload('videoHostImage')}
                  >
                    {watch('videoHostImage') ? (
                      <div className="aspect-ratio-div">
                        <img
                          className="aspect-ratio-container-img"
                          src={watch('videoHostImage') || ''}
                          alt="groupLogoOverlayImage"
                        />
                      </div>
                    ) : (
                      <div className="flex h-56 items-center justify-center">
                        <button className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl">
                          +
                        </button>
                        <input
                          ref={fileInputVideoHostImage}
                          id="file-upload-group-author-logo"
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={(e) => onFileChange(e, 'videoHostImage')}
                          onClick={() => {
                            if (fileInputVideoHostImage.current) {
                              fileInputVideoHostImage.current.value = '';
                            }
                          }}
                        />
                      </div>
                    )}
                  </div>
                  <div className="pl-4">
                    <p className="text-gray-dark pt-4 font-normal">720 x 720 px</p>
                    {watch('videoHostImage') && (
                      <div className="aspect-ratio-container w-full">
                        <button
                          className="my-2 w-full rounded-full bg-gray-300 p-2 text-center text-sm text-white"
                          onClick={() => {
                            setValue('videoHostImage', '', { shouldDirty: true });
                          }}
                        >
                          REMOVE
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="w-1/2">
                <div className="mt-14">
                  <CustomInput
                    inputType={'text'}
                    register={{
                      ...register('videoHostName'),
                    }}
                    Label={'VIDEO HOST NAME'}
                    labelClass="font-medium"
                    disabled={!watch('welcomeVideoEnabled')}
                    dataTestId="video-host-name"
                  />
                </div>
                <div className="mt-4">
                  <CustomInput
                    inputType={'text'}
                    register={{
                      ...register('videoConfigurationTitle'),
                    }}
                    disabled={!watch('welcomeVideoEnabled')}
                    Label={'VIDEO HOST TITLE'}
                    labelClass="font-medium"
                    dataTestId="video-host-title"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 w-full px-7">
          <div className="mt-6 flex w-full items-center justify-between">
            <p className="text-gray-dark pt-4 font-bold">POWERED BY HEADER </p>
            <div>
              <ToggleButton
                id={'PoweredByToggle'}
                setStatus={(e) => {
                  setValue('poweredByHeaderEnabled', e, { shouldDirty: true });
                }}
                status={watch('poweredByHeaderEnabled')}
              />
            </div>
          </div>
          <hr className="bg-gray-dark"></hr>
          <div
            className={`pt-4 ${!watch('poweredByHeaderEnabled') ? 'pointer-events-none cursor-not-allowed bg-white opacity-50' : ''}`}
          >
            <p className="text-red py-2">
              Disclaimer: The logo this image will replace is 180px by 36px, a 5:1 ratio. Upload an image with those
              dimensions for best results
            </p>
            <p className="text-gray-dark pt-1 font-normal">HEADER IMAGE</p>
            <p className="text-gray-dark py-2 pt-1 font-normal">Replaces the Kindly Human Header when active</p>
            <div className="flex w-1/2">
              <div
                className={`${
                  !watch('poweredByHeaderImage') ? 'bg-gray-background-light cursor-pointer' : ''
                } relative h-56 w-full rounded-sm`}
                onClick={() => handleImageUpload('poweredByHeaderImage')}
              >
                {watch('poweredByHeaderImage') ? (
                  <div className="h-full w-full">
                    <img
                      className="h-56 w-auto object-cover"
                      src={watch('poweredByHeaderImage') || ''}
                      alt="poweredByHeaderImage"
                    />
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <button className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl">
                      +
                    </button>
                    <input
                      ref={fileInputPoweredByHeader}
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onFileChange(e, 'poweredByHeaderImage')}
                      onClick={() => {
                        if (fileInputPoweredByHeader.current) {
                          fileInputPoweredByHeader.current.value = '';
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="pl-4">
                <div className="w-full pt-2">
                  {watch('poweredByHeaderImage') && (
                    <button
                      className="my-2 w-full rounded-full bg-gray-300 p-2 text-center text-sm text-white"
                      onClick={() => {
                        setValue('poweredByHeaderImage', '', { shouldDirty: true });
                      }}
                    >
                      REMOVE
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 w-full px-7">
          <div className="mt-6 flex w-full items-center justify-between">
            <p className="text-gray-dark pt-4 font-bold">Group Banner </p>
            <div>
              <ToggleButton
                id={'GroupBannerToggle'}
                setStatus={(e) => {
                  setValue('groupBannerEnabled', e, { shouldDirty: true });
                }}
                status={watch('groupBannerEnabled')}
              />
            </div>
          </div>
          <hr className="bg-gray-dark"></hr>
          <div
            className={`pt-4 ${!watch('groupBannerEnabled') ? 'pointer-events-none cursor-not-allowed bg-white opacity-50' : ''}`}
          >
            <p className="text-red py-2">
              Disclaimer: The banner logo dimensions are not validated. the image ratio on the banner is 3:1. Upload an
              image with that ratio for best results
            </p>
            <p className="text-gray-dark py-3 font-normal">Group Banner Image</p>
            <div className="flex w-1/2">
              <div
                className={`${
                  !watch('groupBannerImage') ? 'bg-gray-background-light cursor-pointer' : ''
                } relative h-56 w-full rounded-sm`}
                onClick={() => handleImageUpload('groupBannerImage')}
              >
                {watch('groupBannerImage') ? (
                  <div className="h-full w-full">
                    <img
                      className="h-56 w-auto object-cover"
                      src={watch('groupBannerImage') || ''}
                      alt="groupBannerImage"
                    />
                  </div>
                ) : (
                  <div className="flex h-full items-center justify-center">
                    <button className="flex h-10 w-10 items-center justify-center rounded-full bg-white text-4xl">
                      +
                    </button>
                    <input
                      ref={fileInputGroupBanner}
                      id="file-upload"
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => onFileChange(e, 'groupBannerImage')}
                      onClick={() => {
                        if (fileInputGroupBanner.current) {
                          fileInputGroupBanner.current.value = '';
                        }
                      }}
                    />
                  </div>
                )}
              </div>
              <div className="pl-4">
                <div className="w-full pt-2">
                  {watch('groupBannerImage') && (
                    <button
                      className="my-2 w-full rounded-full bg-gray-300 p-2 text-center text-sm text-white"
                      onClick={() => {
                        setValue('groupBannerImage', '', { shouldDirty: true });
                      }}
                    >
                      REMOVE
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-4 w-full px-7">
          <p className="text-gray-dark pt-4 font-bold">INTERNAL NOTES</p>
          <hr className="bg-gray-dark"></hr>
          <div className="w-full pt-4">
            <textarea
              value={watch('internalNotes')}
              className="text-gray-dark bg-gray-background-light h-28 w-full rounded-md px-3 py-2 text-sm font-extrabold leading-4 focus:outline-none"
              onChange={(e) => {
                setValue('internalNotes', e.target.value, { shouldDirty: true });
              }}
            />
          </div>
        </div>
      </div>
      <RouteLeavingGuard
        when={isDirty}
        navigate={(path: string) => history.push(path)}
        shouldBlockNavigation={(location: any) => {
          if (location.pathname !== ROUTE_PATH.GROUPS_CONFIGURE) {
            return true;
          }
          return false;
        }}
        titleText={'Alert'}
        contentText={'You have unsaved changes. Do you want to leave this page and lose your changes?'}
        cancelButtonText={'Cancel'}
        confirmButtonText={'Disregard changes'}
        confirmSaveButtonText="Continue and Save changes"
        handleContinueSaveChange={(path) => {
          setSaveLocationPath(path);
          handleSubmit(handleSave)();
        }}
      />
      <Modal
        isModel={tagsModal}
        InnerComponent={
          <div className="inline-block transform overflow-hidden rounded-lg border-4 bg-white text-left align-bottom shadow-xl transition-all sm:my-32 sm:w-full sm:max-w-4xl sm:align-middle">
            <div className="w-full bg-white px-4 pb-4 pt-5 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:ml-4 sm:mt-0 sm:text-left">
                  <div className="sticky top-0 z-50 mb-4 flex justify-between border-b-4 bg-white pb-4">
                    <h3 className="mb-3 text-lg font-bold leading-6 text-gray-900" id="modal-title">
                      Add Listener Tags
                    </h3>
                    <button
                      type="button"
                      className={'rounded-full bg-gray-400 px-5 py-1 font-medium text-white'}
                      onClick={() => {
                        setTagsModal(false);
                      }}
                    >
                      Close
                    </button>
                  </div>
                  <div className="-ml-2 max-h-96 w-full flex-wrap space-x-2 space-y-2 overflow-auto text-sm">
                    {watch('tagsArray')?.length > 0 &&
                      watch('tagsArray')
                        .sort((a: any, b: any) => a.name.localeCompare(b.name))
                        .map((item: any, index: number) => {
                          return (
                            <button
                              key={`status_${index}`}
                              className={`${
                                item.hasTag ? 'bg-citrus' : 'bg-gray-background-light'
                              } rounded-md text-sm font-bold text-black ${index === 0 ? 'ml-2 mt-2' : ' '}`}
                              onClick={() => {
                                handleTags(!item.hasTag, item.id);
                              }}
                            >
                              <div className="flex px-3 py-2">
                                <div className="grow pr-2">{item.name}</div>
                              </div>
                            </button>
                          );
                        })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }
      />
    </div>
  );
};

export default Configure;
